import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { EdLogo } from "../components/shell/EdLogo";
import { Seo } from "../seo/Seo";
import { trackCtaClick, type CtaLocation } from "../lib/analytics";
import { useScrollDepth } from "../hooks/use-scroll-depth";
import { LandingFaq } from "../components/landing/LandingFaq";
import { PlaygroundDemo } from "../components/landing/PlaygroundDemo";
import { StickyMobileCta } from "../components/landing/StickyMobileCta";

function ctaHandler(location: CtaLocation, target: "register" | "login") {
  return () => trackCtaClick(location, { target });
}

const TOOLS = [
  {
    n: "01",
    title: "Releases",
    body: "AI drafts notes from commits. You polish. Stakeholders read.",
    color: "var(--color-signal-red)",
  },
  {
    n: "02",
    title: "Feature flags",
    body: "PMs ship without a deploy. Devs read flags via SDK or REST.",
    color: "var(--color-signal-ok)",
  },
  {
    n: "03",
    title: "API mocks",
    body: "Spin a mocked endpoint in fifteen seconds. Share the URL.",
    color: "var(--color-signal-warn)",
  },
  {
    n: "04",
    title: "Webhook inbox",
    body: "See every payload. Replay any of them. Bypass to local.",
    color: "var(--color-signal-lavender)",
  },
];

export function LandingPage() {
  useScrollDepth();
  return (
    <div className="min-h-screen bg-paper text-foreground flex flex-col">
      <Seo route="/" />
      {/* Nav */}
      <header className="border-b border-rule">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-5 flex items-center justify-between">
          <EdLogo size={17} />
          <nav className="hidden md:flex gap-7 text-sm text-muted-foreground items-center">
            <Link to="/releases">Releases</Link>
            <Link to="/flags">Flags</Link>
            <Link to="/mocks">API mocks</Link>
            <Link to="/webhooks">Webhooks</Link>
          </nav>
          <div className="flex gap-3 items-center">
            <Link
              to="/login"
              className="text-sm"
              onClick={ctaHandler("header", "login")}
            >
              Sign in
            </Link>
            <Link to="/register" onClick={ctaHandler("header", "register")}>
              <Button size="sm">Start free →</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Beta strip */}
      <div className="bg-signal-red-soft text-foreground border-b border-rule">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-2.5 text-xs sm:text-sm">
          <span>
            <b className="text-signal-red">● Beta</b> &nbsp; Free for every
            team and project.{" "}
            <span className="font-serif italic">No card. No caps.</span>
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="flex-1">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-12 sm:py-20 grid lg:grid-cols-[1fr_440px] gap-12">
        <div>
          <span className="text-eyebrow text-signal-red">
            ∗ A growing toolkit, organized by team & project
          </span>
          <h1 className="mt-6 text-5xl sm:text-7xl lg:text-[5.5rem] font-semibold leading-[0.95] tracking-[-0.04em]">
            The{" "}
            <span className="font-serif italic font-normal">boring side</span>
            <br />
            of shipping,
            <br />
            <span className="text-signal-red">sorted.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-foreground max-w-lg font-medium">
            One workspace for four developer tools: a webhook inbox, API mocks,
            feature flags, and release notes.
          </p>
          <p className="mt-3 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-lg font-serif">
            A webhook inbox that&rsquo;s a delight to debug with. Mocked
            endpoints in fifteen seconds. Feature flags PMs can toggle. Release
            notes stakeholders actually read. All scoped to the{" "}
            <em>project</em> you&rsquo;re working on.
          </p>
          <div className="mt-8 flex gap-3">
            <Link to="/register" onClick={ctaHandler("hero", "register")}>
              <Button size="lg">Start free →</Button>
            </Link>
            <Link to="/login" onClick={ctaHandler("hero", "login")}>
              <Button size="lg" variant="outline">
                Sign in
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex gap-9 font-mono text-xs text-faint flex-wrap">
            <span>
              ★ Customer #4? <span className="text-foreground">It could be you.</span>
            </span>
            <span>⌘ open SDK · 12h webhook TTL · no card on file</span>
          </div>
        </div>

        {/* Hero card — real product screenshot of the webhook viewer.
            Generated by `bun run capture:landing`; the source script is at
            tests/scripts/capture-landing-assets.spec.ts. */}
        <div className="hidden lg:flex items-end">
          <div className="bg-card-paper border border-rule rounded-xl shadow-lg overflow-hidden w-full">
            <div className="px-4 py-3 border-b border-rule-soft flex items-center justify-between bg-panel">
              <div className="flex items-center gap-2">
                <span className="size-1.5 rounded-sm bg-signal-ok animate-pulse" />
                <span className="text-eyebrow">
                  Webhook inbox · live
                </span>
              </div>
              <span className="font-mono text-[10px] text-faint">
                4 events captured
              </span>
            </div>
            <img
              src="/landing/webhook-viewer.png"
              alt="Swisstools webhook inbox showing four captured Stripe events with a selected charge.refunded payload"
              width={1280}
              height={800}
              loading="lazy"
              decoding="async"
              className="w-full block"
            />
            <div className="px-4 py-3 border-t border-rule-soft flex justify-between items-center bg-panel">
              <span className="font-mono text-[10.5px] text-faint">
                → stripe inbox · Checkout 2.0
              </span>
              <span className="font-sans text-xs text-signal-red font-semibold">
                Open inbox →
              </span>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Tools strip */}
      <section className="border-t border-b border-rule">
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {TOOLS.map((t, i) => (
            <div
              key={t.n}
              className={`p-6 ${i < TOOLS.length - 1 ? "lg:border-r" : ""} border-rule`}
            >
              <span
                className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase"
                style={{ color: t.color }}
              >
                <span
                  className="size-1.5 rounded-sm"
                  style={{ background: t.color }}
                />
                {t.n} · {t.title}
              </span>
              <div className="font-serif text-xl font-semibold tracking-tight mt-3 mb-2">
                {t.title}
              </div>
              <div className="text-sm leading-relaxed text-muted-foreground">
                {t.body}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Live playground — anonymous webhook inbox right on the landing page */}
      <PlaygroundDemo />

      {/* How it works */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-12 sm:py-16">
          <span className="text-eyebrow text-signal-red">
            ∗ Live in under a minute
          </span>
          <div className="mt-8 grid gap-8 sm:grid-cols-3">
            {[
              {
                n: "1",
                title: "Sign in with GitHub",
                body: "No credit card, no setup wizard. A personal team is ready the moment you land.",
              },
              {
                n: "2",
                title: "Create a project",
                body: "Everything — mocks, webhooks, flags, releases — is scoped to the project you're working on.",
              },
              {
                n: "3",
                title: "Grab your URL",
                body: "Spin a mocked endpoint or a webhook inbox and share the link. That's the whole ceremony.",
              },
            ].map((s) => (
              <div key={s.n}>
                <div className="font-mono text-3xl font-semibold text-signal-red tabular-nums">
                  {s.n}
                </div>
                <div className="font-serif text-lg font-semibold tracking-tight mt-2 mb-1.5">
                  {s.title}
                </div>
                <div className="text-sm leading-relaxed text-muted-foreground">
                  {s.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LandingFaq />

      {/* Final CTA */}
      <section className="border-b border-rule bg-signal-red-soft">
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-12 sm:py-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-[-0.03em]">
              Stop hand-rolling the{" "}
              <span className="font-serif italic font-normal">boring parts</span>
              .
            </h2>
            <p className="mt-2 text-muted-foreground">
              Free for every team and project while in beta. No card. No caps.
            </p>
          </div>
          <Link
            to="/register"
            className="shrink-0"
            onClick={ctaHandler("footer", "register")}
          >
            <Button size="lg">Start free →</Button>
          </Link>
        </div>
      </section>

      <footer>
        <div className="max-w-7xl mx-auto w-full px-5 sm:px-12 py-5 font-mono text-xs text-faint">
          <span>
            Swisstools — five tools, one tab. Apologies to your bookmarks bar.
          </span>
        </div>
      </footer>

      <StickyMobileCta />
    </div>
  );
}
